<!-- app-subdomain-creators/src/Components/NewsfeedCreatorSavedTab.vue -->
<template>
  <div class=" lg:w-[1175px] mx-auto pb-10 pt-5 min-h-[650px]">
    <!-- Loading state -->
    <div
      v-if="isLoading && !savedPosts.length"
      class="space-y-4"
    >
      <div
        v-for="n in 3"
        :key="n"
        class="h-32 rounded-xl bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 animate-pulse"
      ></div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!savedPosts.length"
      class="flex flex-col items-center -ml-40 py-16 text-center text-zinc-500"
    >
      <p class="text-lg font-semibold mb-2 ">No saved posts yet</p>
      <p class="text-sm text-zinc-400 max-w-md">
        Save posts from the Discussion tab using the menu on each post. They’ll
        show up here for quick access.
      </p>
    </div>

    <!-- Saved posts list -->
<div v-else class="space-y-4 max-w-[700px]">
  <div
    v-for="post in savedPosts"
    :key="post.id"
    class="relative"
  >
    <NewsfeedCreatorPost
      :post="post"
      :featureInstanceId="featureInstanceId"
      :postingAsName="postingAsName"
      :postingAsAvatarUrl="postingAsAvatarUrl"
      context="saved"
      :is-saved-context="true"
      @edit-post="handleEditPostFromSaved"
      @unsave-post="handleChildUnsavePost"
      @delete-post="handleDeletePostFromSaved"
    />
  </div>

      <div
        v-if="hasMore"
        ref="infiniteScrollSentinel"
        class="flex justify-center pt-4 h-16"
      >
        <span class="text-sm text-zinc-400">
          {{ isLoadingMore ? 'Loading...' : 'Scroll for more' }}
        </span>
      </div>
    </div>
  </div>

  <!-- 🔹 Add just before </template> or at the end inside root -->
<NewsfeedDeletePostConfirmModal
  :is-open="deleteModalOpen"
  :is-submitting="isDeletingPost"
  @cancel="handleCancelDelete"
  @confirm="handleConfirmDelete"
/>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import NewsfeedCreatorPost from '@/Components/NewsfeedCreatorPost.vue';
import NewsfeedDeletePostConfirmModal from '@/Components/NewsfeedDeletePostConfirmModal.vue';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(['open-edit-post']);

const savedPosts = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(false);
const unsavingId = ref(null);

const limit = 10;
const offset = ref(0);
const infiniteScrollSentinel = ref(null);
let infiniteObserver = null;
let sentinelReadyForNextLoad = true;
const INFINITE_LOAD_COOLDOWN_MS = 500;
let lastInfiniteLoadAt = 0;
let inflightAppendOffset = null;


// 🔹 Delete modal state
const deleteModalOpen = ref(false);
const deleteTargetPostId = ref(null);
const isDeletingPost = ref(false);

// Same default avatar as Discussion tab
const defaultAvatar = new URL(
  '@/assets/media/images/default-avatar.png',
  import.meta.url
).href;

const buildCoverImageUrl = (fileId) => {
  if (!fileId) return null;
  return `${apiProxyUrl}/api/image/${fileId}`;
};

const handleEditPostFromSaved = ({ postId }) => {
  if (!postId) return;
  const postToEdit = savedPosts.value.find((p) => String(p.id) === String(postId));
  if (!postToEdit) return;
  emit('open-edit-post', { post: { ...postToEdit } });
};

// Mirror the normalization logic from NewsfeedCreatorDiscussionTab
function normalizeSavedPost(p) {
  if (!p) return null;

  const currentUserName = props.postingAsName || 'You';
  const currentUserAvatar = props.postingAsAvatarUrl || defaultAvatar;

  // 🔹 Raw dates from backend (support camel + snake)
  const rawCreated = p.createdAt || p.created_at || null;
  const rawSaved   = p.savedAt   || p.saved_at   || null;

  const createdDate = rawCreated ? new Date(rawCreated) : null;
  const savedDate   = rawSaved   ? new Date(rawSaved)   : null;

  const createdAtMs =
    createdDate && !Number.isNaN(createdDate.getTime())
      ? createdDate.getTime()
      : 0;

  const savedAtMs =
    savedDate && !Number.isNaN(savedDate.getTime())
      ? savedDate.getTime()
      : 0;

  // Normalize attachments from backend
  const attachments = Array.isArray(p.attachments)
    ? p.attachments
        .map((att) => {
          if (!att) return null;
          const fileId = att.fileId || att.file_id || att.mediaFileId || null;
          if (!fileId) return null;
          return { ...att, fileId: String(fileId) };
        })
        .filter(Boolean)
    : [];

  // Derive coverMediaFileId – prefer explicit, else first attachment
  const coverMediaFileId =
    p.coverMediaFileId ||
    p.cover_media_file_id ||
    (attachments.length ? attachments[0].fileId : null);

  const imageUrl = coverMediaFileId
    ? buildCoverImageUrl(coverMediaFileId)
    : null;

  // Typed reaction counts
  const reactionsSummary = p.reactionsSummary || p.reactions || {
    total: 0,
    like: 0,
    love: 0,
    care: 0,
    haha: 0,
    wow: 0,
    sad: 0,
    angry: 0,
  };

  // Typed reaction user lists (for tooltips)
  const reactionUsersByType = p.reactionUsersByType || {
    like: [],
    love: [],
    care: [],
    haha: [],
    wow: [],
    sad: [],
    angry: [],
  };

  const reactionNamesByType = {};
  for (const key of ['like', 'love', 'care', 'haha', 'wow', 'sad', 'angry']) {
    const arr = Array.isArray(reactionUsersByType[key])
      ? reactionUsersByType[key]
      : [];
    reactionNamesByType[key] = arr
      .map((u) => (u && typeof u.name === 'string' ? u.name.trim() : ''))
      .filter(Boolean);
  }

  // Legacy flat list if anything still uses it
  const reactionNamesLegacy = Object.values(reactionNamesByType).flat();

  return {
    id: p.id,

    // "posted as" identity
    authorName: p.postedAsName || currentUserName,
    authorAvatar: p.postedAsAvatarUrl || defaultAvatar,

    // Keep original string date + numerical timestamp
    createdAt: rawCreated,
    createdAtMs,
    timeAgo: rawCreated || '',

    body: p.content || p.body || '',

    reactionsSummary,
    reactionsCount: Number(reactionsSummary.total) || 0,

    commentsCount: p.commentsCount ?? 0,
    sendCount: 0,

    reactionNamesByType,
    reactionNames: reactionNamesLegacy,

    hasMedia: attachments.length > 0 || !!coverMediaFileId,
    imageUrl,
    coverMediaFileId,
    attachments,
    poll: p.poll || p.pollData || p.postPoll || null,
    postType: p.postType || p.post_type || (p.poll ? 'poll' : 'text'),
    pollOptions: Array.isArray(p.pollOptions) ? p.pollOptions : null,

    currentUserName,
    currentUserAvatar,

    // Saved-specific flags
    isSaved: !!(p.isSaved ?? p.is_saved ?? true),
    savedAt: rawSaved,
    savedAtMs,

    raw: p,
  };
}

function mergeUpdatedPostWithExisting(existingPost, normalizedPost, rawUpdate = null) {
  const existing = existingPost || {};
  const normalized = normalizedPost || {};
  const raw = rawUpdate && typeof rawUpdate === 'object' ? rawUpdate : {};

  const merged = { ...existing, ...normalized };

  if (!merged.createdAt) merged.createdAt = existing.createdAt || null;
  if (!merged.timeAgo) merged.timeAgo = existing.timeAgo || merged.createdAt || '';
  if (!merged.authorName) merged.authorName = existing.authorName || null;
  if (!merged.authorAvatar) merged.authorAvatar = existing.authorAvatar || null;
  if (!merged.savedAt) merged.savedAt = existing.savedAt || null;
  if (!merged.savedAtMs) merged.savedAtMs = existing.savedAtMs || 0;

  const hasRawAttachments = Array.isArray(raw.attachments);
  const hasUsableRawAttachments =
    hasRawAttachments &&
    raw.attachments.some((att) => {
      if (!att || typeof att !== 'object') return false;
      // fileId-only attachment objects are common in edit payloads and should not
      // be treated as a full media replacement.
      return !!(
        att.url ||
        att.fileUrl ||
        att.file_url ||
        att.videoUrl ||
        att.secureVideoUrl ||
        att.hlsPlaylistUrl ||
        att.hls_playlist_url ||
        att.thumbnailUrl ||
        att.posterUrl ||
        att.mimeType ||
        att.mime ||
        att.contentType ||
        att.fileType ||
        att.type
      );
    });
  const rawCoverMediaValue =
    raw.coverMediaFileId ??
    raw.cover_media_file_id ??
    null;
  const existingCoverMediaValue = existing.coverMediaFileId ?? null;
  const hasRawCoverMedia =
    (Object.prototype.hasOwnProperty.call(raw, 'coverMediaFileId') ||
      Object.prototype.hasOwnProperty.call(raw, 'cover_media_file_id')) &&
    String(rawCoverMediaValue || '') !== String(existingCoverMediaValue || '');
  const hasRawMediaAdds = Array.isArray(raw.mediaAddFileIds) && raw.mediaAddFileIds.length > 0;
  const hasRawMediaRemoves =
    Array.isArray(raw.mediaRemoveFileIds) && raw.mediaRemoveFileIds.length > 0;
  const hasAnyRawMediaMutation =
    hasRawCoverMedia ||
    hasUsableRawAttachments ||
    hasRawMediaAdds ||
    hasRawMediaRemoves;

  const normalizedHasMedia =
    !!normalized.coverMediaFileId ||
    !!normalized.imageUrl ||
    (Array.isArray(normalized.attachments) && normalized.attachments.length > 0) ||
    !!normalized.hasMedia;
  const existingHasMedia =
    !!existing.coverMediaFileId ||
    !!existing.imageUrl ||
    (Array.isArray(existing.attachments) && existing.attachments.length > 0) ||
    !!existing.hasMedia;

  if (!hasAnyRawMediaMutation || (!normalizedHasMedia && existingHasMedia && !hasRawMediaRemoves)) {
    merged.attachments = Array.isArray(existing.attachments) ? existing.attachments : [];
    merged.coverMediaFileId = existing.coverMediaFileId || null;
    merged.imageUrl = existing.imageUrl || null;
    merged.hasMedia =
      !!existing.hasMedia ||
      !!existing.coverMediaFileId ||
      (Array.isArray(existing.attachments) && existing.attachments.length > 0);
  }

  const hasRawPollMutation =
    Object.prototype.hasOwnProperty.call(raw, 'poll') ||
    Object.prototype.hasOwnProperty.call(raw, 'postType') ||
    Object.prototype.hasOwnProperty.call(raw, 'post_type') ||
    Object.prototype.hasOwnProperty.call(raw, 'pollOptions') ||
    Object.prototype.hasOwnProperty.call(raw, 'poll_options');
  const normalizedHasPoll =
    !!normalized.poll ||
    String(normalized.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(normalized.pollOptions) && normalized.pollOptions.length > 0);
  const existingHasPoll =
    !!existing.poll ||
    String(existing.postType || '').toLowerCase() === 'poll' ||
    (Array.isArray(existing.pollOptions) && existing.pollOptions.length > 0);

  if (!hasRawPollMutation || (!normalizedHasPoll && existingHasPoll)) {
    merged.poll = existing.poll || null;
    merged.postType = existing.postType || merged.postType || null;
    merged.pollOptions = Array.isArray(existing.pollOptions)
      ? existing.pollOptions
      : merged.pollOptions || null;
  }

  // Saved tab invariant.
  merged.isSaved = true;

  return merged;
}

async function addOrUpdatePostFromUpdate(updatedPost) {
  const post = updatedPost && typeof updatedPost === 'object' ? updatedPost : null;
  if (!post) return false;

  const status = String(post.status || post.postStatus || '').toLowerCase();
  if (status && status !== 'published') return false;

  const normalized = normalizeSavedPost({ ...post, isSaved: true });
  if (!normalized?.id) return false;

  const idx = savedPosts.value.findIndex((p) => String(p.id) === String(normalized.id));
  if (idx < 0) return false;

  const next = [...savedPosts.value];
  next[idx] = mergeUpdatedPostWithExisting(next[idx], normalized, post);
  next.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
  savedPosts.value = next;
  return true;
}


async function fetchSavedPosts(options = { reset: false }) {
  const { reset } = options || {};

  if (!props.featureInstanceId) {
    savedPosts.value = [];
    hasMore.value = false;
    inflightAppendOffset = null;
    return;
  }

  if (reset) {
    offset.value = 0;
  }

  const isFirstPage = offset.value === 0;
  if (isFirstPage) {
    isLoading.value = true;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset.value));
    params.set('featureInstanceId', String(props.featureInstanceId));

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/saved-posts?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorSavedTab] saved-posts backend NOT OK:',
        res.status,
        text
      );
      if (isFirstPage) {
        savedPosts.value = [];
      }
      hasMore.value = false;
      return;
    }

    const data = await res.json();
    const rawPosts = Array.isArray(data.posts) ? data.posts : [];

    // every post in this tab is saved for this user
    const normalized = rawPosts
      .map((p) => normalizeSavedPost({ ...p, isSaved: true }))
      .filter(Boolean);

    if (reset) {
          savedPosts.value = normalized;
        } else {
          savedPosts.value = [...savedPosts.value, ...normalized];
        }

        // 🔽 Ensure newest at top: prefer savedAt, then createdAt
// 🔽 Ensure newest at top by POST CREATION DATE ONLY
savedPosts.value.sort((a, b) => {
  const aKey = a.createdAtMs || 0;
  const bKey = b.createdAtMs || 0;

  // Newest (largest timestamp) first
  return bKey - aKey;
});



    // If we got a full page, assume there may be more
    hasMore.value = normalized.length === limit;
    if (normalized.length > 0) {
      offset.value += normalized.length;
    }
  } catch (err) {
    console.error('[NewsfeedCreatorSavedTab] fetchSavedPosts error:', err);
    if (offset.value === 0) {
      savedPosts.value = [];
    }
    hasMore.value = false;
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
    if (!isFirstPage) {
      inflightAppendOffset = null;
    }
  }
}

function loadMore() {
  if (!hasMore.value || isLoadingMore.value) return Promise.resolve();
  return fetchSavedPosts({ reset: false });
}

function setupInfiniteScrollObserver() {
  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }
  if (!infiniteScrollSentinel.value) return;

  const rootEl =
    document.querySelector('.app-scroll') ||
    document.querySelector('#app-scroll') ||
    null;

  infiniteObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (!entry.isIntersecting) {
        sentinelReadyForNextLoad = true;
        return;
      }

      if (!sentinelReadyForNextLoad) return;
      if (!hasMore.value || isLoading.value || isLoadingMore.value) return;
      if (inflightAppendOffset === offset.value) return;
      if (Date.now() - lastInfiniteLoadAt < INFINITE_LOAD_COOLDOWN_MS) return;

      sentinelReadyForNextLoad = false;
      inflightAppendOffset = offset.value;
      lastInfiniteLoadAt = Date.now();
      loadMore().finally(() => {
        inflightAppendOffset = null;
      });
    },
    {
      root: rootEl,
      rootMargin: '0px 0px 600px 0px',
      threshold: 0.01,
    }
  );
  infiniteObserver.observe(infiniteScrollSentinel.value);
}

async function unsavePost(post) {
  if (!post || !post.id) return;

  const postId = post.id;
  if (unsavingId.value) return;
  unsavingId.value = postId;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error(
        '[NewsfeedCreatorSavedTab] unsave backend NOT OK:',
        res.status,
        text
      );
      alert('Failed to unsave post. Please try again.');
      return;
    }

    // Remove from local list
    savedPosts.value = savedPosts.value.filter((p) => p.id !== postId);
  } catch (err) {
    console.error('[NewsfeedCreatorSavedTab] unsave error:', err);
    alert('Error removing saved post. Please try again.');
  } finally {
    unsavingId.value = null;
  }
}

function handleChildUnsavePost({ postId }) {
  if (!postId) return;

  const post = savedPosts.value.find(
    (p) => String(p.id) === String(postId)
  );
  if (!post) return;

  // reuse existing backend call + UI removal
  unsavePost(post);
}

/**
 * 🔹 Delete flow for Saved tab
 *   (similar to Discussion / Your Posts)
 */

const handleDeletePostFromSaved = ({ postId }) => {
  if (!postId) return;
  deleteTargetPostId.value = postId;
  deleteModalOpen.value = true;
};

const handleCancelDelete = () => {
  deleteModalOpen.value = false;
  deleteTargetPostId.value = null;
  isDeletingPost.value = false;
};

const handleConfirmDelete = async () => {
  const postId = deleteTargetPostId.value;
  if (!postId) return;

  isDeletingPost.value = true;

  try {
    const resp = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${postId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await resp.json().catch(() => ({}));

    if (!resp.ok || !data.success) {
      console.error(
        '[NewsfeedCreatorSavedTab] delete post failed:',
        resp.status,
        data
      );
      // optional: toast
      return;
    }

    // ✅ Remove from Saved tab list
    savedPosts.value = savedPosts.value.filter(
      (p) => String(p.id) !== String(postId)
    );

    deleteModalOpen.value = false;
    deleteTargetPostId.value = null;
  } catch (err) {
    console.error('[NewsfeedCreatorSavedTab] error deleting post:', err);
    // optional: toast
  } finally {
    isDeletingPost.value = false;
  }
};


// Load when this tab mounts & instanceId exists
onMounted(() => {
  if (props.featureInstanceId) {
    fetchSavedPosts({ reset: true });
  }
  nextTick(() => {
    setupInfiniteScrollObserver();
  });
});

// If user switches to another newsfeed instance while on Saved tab
watch(
  () => props.featureInstanceId,
  (newVal, oldVal) => {
    if (!newVal) {
      savedPosts.value = [];
      hasMore.value = false;
      offset.value = 0;
      inflightAppendOffset = null;
      return;
    }
    if (newVal !== oldVal) {
      sentinelReadyForNextLoad = true;
      inflightAppendOffset = null;
      fetchSavedPosts({ reset: true });
      nextTick(() => {
        setupInfiniteScrollObserver();
      });
    }
  }
);
watch(
  () => savedPosts.value.length,
  async () => {
    await nextTick();
    setupInfiniteScrollObserver();
  }
);
watch(
  () => infiniteScrollSentinel.value,
  (el) => {
    if (el) setupInfiniteScrollObserver();
    else if (infiniteObserver) {
      infiniteObserver.disconnect();
      infiniteObserver = null;
    }
  }
);

onBeforeUnmount(() => {
  if (infiniteObserver) {
    infiniteObserver.disconnect();
    infiniteObserver = null;
  }
  inflightAppendOffset = null;
  sentinelReadyForNextLoad = true;
});

// 👇 Add this block at the very bottom of <script setup>
defineExpose({
  reloadPosts() {
    // always reset so we get a fresh list when the tab is activated
    fetchSavedPosts({ reset: true });
  },
  addOrUpdatePostFromUpdate,
});


</script>

